import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Spin, Table } from 'antd';
import styled from '@emotion/styled';
import { LeftOutlined, PlusOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import { CustomModal } from 'components/molecules/PricingSimulator/Modals/ScenarioColumnEditModal';
import { CustomSelect } from 'components/molecules/PricingSimulator/DimSection';
import ConfirmationModal from 'components/molecules/common/modals/ConfirmationModal';
import DownSvg from 'assets/svg/Down';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { transformFiltersV2 } from 'utils/helperMethods';
import { customGroupsUrls } from 'config/urls';
import {
    getCustomProductGroupColumns,
    getCustomProductGroupDim1Columns,
    getCustomProductGroupDim1SelectedColumns,
} from 'utils/getTradePromoOptimizationTableColumns';
import { TPODataContext } from 'contexts/TPOContext';
import Footer from '../../PricingSimulator/Footer';
import NewGlobalFilter from '../../common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from '../../PricingSimulator/FilterRibbon/FilterRibbon';
import InfiniteScroller from '../../InfiniteScroller';
import useFilterState from './hooks/useFilterState';
import useCustomProductGroupModal from './hooks/useCustomProductGroupModal';
import useCustomProductGroupDelete from './hooks/useCustomProductGroupDelete';
import { CUSTOM_GROUPS_QUERY_KEY } from './constants';
import type { CustomProductGroupRecord } from './types';

export type { CustomProductGroupRecord };

const TABLE_ID = 'custom-product-group-table-id';
const PAGE_LIMIT = 100;

const ModalTable = styled(Table)`
    .ant-table {
        font-size: 12px !important;
    }
    .ant-table-thead > tr > th {
        background: #fafafa !important;
        font-size: 12px !important;
        font-weight: 600 !important;
        padding: 8px 12px !important;
    }
    .ant-table-tbody > tr > td {
        padding: 8px 12px !important;
        font-size: 12px !important;
    }
` as any;

const CustomProductGroupContainer: React.FC = () => {
    // RBAC: reuse TPO-level rbacConditions to control write access (manage/edit/delete).
    // showCreatePlanButton is false when the user lacks write permissions for this customer.
    const { rbacConditions, rbacLoading } = useContext(TPODataContext);
    const canManageGroups = !rbacLoading && (rbacConditions?.showCreatePlanButton !== false);

    const [searchText, setSearchText] = useState('');
    const [tableVariables, setTableVariables] = useState<any>({
        limit: PAGE_LIMIT,
        offset: 0,
        filters: {},
        search_key: '',
    });

    const tableHeight = useRef<number | null>(0);
    tableHeight.current = useFillHeightToFitScreen({
        id: TABLE_ID,
        height: tableHeight.current,
        buffer: 110,
    });

    const { loading: dim2OptionsLoading, data: rawDim2Options } = useCommonApiHandler({
        method: 'get',
        url: customGroupsUrls.getDim2OptionsUrl,
        queryKey: ['custom-groups-dim2-options'],
        parserFunction: (res: any) => res?.options ?? [],
    });

    const { data: rawUiDimensions } = useCommonApiHandler({
        method: 'get',
        url: customGroupsUrls.uiDimensionsUrl,
        queryKey: ['custom-groups-ui-dimensions'],
        parserFunction: (res: any) => res,
    });

    const { loading: tableDataLoading, data: rawTableResponse } = useCommonApiHandler({
        method: 'post',
        url: customGroupsUrls.indexUrl,
        queryKey: [CUSTOM_GROUPS_QUERY_KEY, tableVariables],
        variables: tableVariables,
        parserFunction: (res: any) => ({ rows: res?.rows ?? [], total: res?.total ?? 0 }),
    });

    const [tableData, setTableData] = useState<CustomProductGroupRecord[]>([]);
    const totalCount: number = rawTableResponse?.total ?? 0;

    // added comment to trigger a build
    useEffect(() => {
        if (!rawTableResponse) return;
        if (tableVariables.offset === 0) {
            setTableData(rawTableResponse.rows);
        } else {
            setTableData((prev) => [...prev, ...rawTableResponse.rows]);
        }
    }, [rawTableResponse]);
    const dim2Options = useMemo(() => rawDim2Options ?? [], [rawDim2Options]);

    const uiDimensionConfig = useMemo(() => {
        if (!rawUiDimensions || !Array.isArray(rawUiDimensions)) return null;
        const filterKeys = rawUiDimensions
            .filter((item: any) => item.is_filter)
            .map((item: any) => ({ label: item.label, value: item.value, dimension: item.dimension }));
        const filterDimensionInfo = rawUiDimensions
            .filter((item: any) => item.is_lowest)
            .reduce((acc: any, item: any) => {
                acc[item.dimension] = { label: item.label, key: item.value };
                return acc;
            }, {});
        return { filterKeys, filterDimensionInfo };
    }, [rawUiDimensions]);

    const modalFilterKeys = useMemo(
        () => (uiDimensionConfig?.filterKeys ?? []).filter((k: any) => k.dimension === 'dim1'),
        [uiDimensionConfig?.filterKeys],
    );

    const modalFilterDimInfo = useMemo(() => {
        const info: any = {};
        if (uiDimensionConfig?.filterDimensionInfo?.dim1) {
            info.dim1 = uiDimensionConfig.filterDimensionInfo.dim1;
        }
        return info;
    }, [uiDimensionConfig?.filterDimensionInfo]);

    // Hooks
    const mainFilter = useFilterState(uiDimensionConfig?.filterKeys ?? []);

    const handleTableReset = useCallback(() => {
        setTableData([]);
        setTableVariables((prev: any) => ({ ...prev, offset: 0 }));
    }, []);

    const modal = useCustomProductGroupModal({ modalFilterKeys, onMutationSuccess: handleTableReset });
    const deleteHandler = useCustomProductGroupDelete(handleTableReset);

    useEffect(() => {
        mainFilter.reset();
    }, [mainFilter.reset]);

    useEffect(() => {
        if (!uiDimensionConfig?.filterKeys?.length) return;
        setTableVariables((prev: any) => ({ ...prev, filters: mainFilter.currentFilters, offset: 0 }));
    }, [mainFilter.currentFilters]);

    const onNext = useCallback(() => {
        const length = tableData.length;

        if (tableDataLoading || length === 0 || length >= totalCount) return;

        setTableVariables((prev: any) => ({
            ...prev,
            offset: prev.offset + prev.limit,
        }));
    }, [tableDataLoading, tableData.length, totalCount]);

    // lets see
    const a = 10;
    const apple = 123;

    // Filter API variables
    const filterApiVariables = useMemo(() => {
        if (!mainFilter.selectedFilter?.value || !uiDimensionConfig?.filterKeys) return undefined;
        const updatedFilters = transformFiltersV2(
            mainFilter.tempApplyFilterObject,
            uiDimensionConfig.filterKeys,
            mainFilter.selectedFilter.value,
        );
        return {
            attribute_type: mainFilter.selectedFilter.value,
            limit: 200,
            filters: updatedFilters,
        };
    }, [mainFilter.selectedFilter, mainFilter.tempApplyFilterObject, uiDimensionConfig?.filterKeys]);

    const modalAttrNamesApiVars = useMemo(() => {
        if (!modal.modalFilter.selectedFilter?.value || modal.selectedDim2Id == null) return undefined;
        const updatedFilters = transformFiltersV2(
            modal.modalFilter.tempApplyFilterObject,
            modalFilterKeys,
            modal.modalFilter.selectedFilter.value,
        );
        const groupId = !modal.isCreatingNew && modal.selectedGroupValue ? Number(modal.selectedGroupValue) : undefined;
        return {
            dim2_id: modal.selectedDim2Id,
            attribute_type: modal.modalFilter.selectedFilter.value,
            limit: 200,
            filters: updatedFilters,
            ...(groupId != null ? { group_id: groupId } : {}),
        };
    }, [
        modal.modalFilter.selectedFilter,
        modal.modalFilter.tempApplyFilterObject,
        modalFilterKeys,
        modal.selectedDim2Id,
        modal.isCreatingNew,
        modal.selectedGroupValue,
    ]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setTableVariables((prev: any) => ({ ...prev, search_key: searchText.trim(), offset: 0 }));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchText]);

    // Derived values
    const columns = useMemo(
        () => getCustomProductGroupColumns(modal.openEditModal, deleteHandler.handleDelete, canManageGroups),
        [modal.openEditModal, deleteHandler.handleDelete, canManageGroups],
    );

    const selectedItemColumns = useMemo(() => getCustomProductGroupDim1SelectedColumns(), []);
    const availableItemColumns = useMemo(() => getCustomProductGroupDim1Columns(), []);

    const footerText = `Showing ${tableData.length.toLocaleString()} of ${totalCount.toLocaleString()} records`;

    return (
        <div className="relative">
            <div className="flex justify-end mt-12 mb-2 items-center">
                <div className="flex gap-2 items-center">
                    <Input
                        placeholder="Search"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        prefix={<SearchOutlined />}
                        allowClear
                        style={{ borderRadius: 6, width: 180 }}
                    />
                    <NewGlobalFilter
                        availableFilters={uiDimensionConfig?.filterKeys ?? []}
                        allCheckedAttribute={mainFilter.allCheckedAttribute}
                        allSelectedValueForAllAttribute={mainFilter.allSelectedValueForAllAttribute}
                        applyFilterObject={mainFilter.applyFilterObject}
                        resetFilterObject={mainFilter.reset}
                        onSubmitHandler={mainFilter.onSave}
                        url={customGroupsUrls.getAttributeNamesUrl}
                        selectedFilter={mainFilter.selectedFilter}
                        setSelectedFilter={mainFilter.setSelectedFilter}
                        tempApplyFilterObject={mainFilter.tempApplyFilterObject}
                        setTempApplyFilterObject={mainFilter.setTempApplyFilterObject}
                        apiVariables={filterApiVariables}
                        filterDimensionInfo={uiDimensionConfig?.filterDimensionInfo}
                        showAttributePanelToLeft
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={modal.openAddModal}
                        disabled={!canManageGroups || rbacLoading}
                        style={{ borderRadius: 6 }}
                    >
                        Manage Groups
                    </Button>
                </div>
            </div>

            {/* Filter ribbon */}
            <div className="my-3">
                <FilterRibbon
                    allCheckedAttribute={mainFilter.allCheckedAttribute}
                    allSelectedValueForAllAttribute={mainFilter.allSelectedValueForAllAttribute}
                    applyFilterObject={mainFilter.applyFilterObject}
                    resetFilterObject={mainFilter.reset}
                    onSubmitHandler={mainFilter.onSave}
                    filterKeys={uiDimensionConfig?.filterKeys}
                />
            </div>

            {/* Main table */}
            <InfiniteScroller
                data={tableData}
                loading={tableDataLoading}
                hasMore={tableData.length < totalCount || tableDataLoading}
                height={
                    tableData.length
                        ? tableHeight.current ?? undefined
                        : isNaN(Number(tableHeight.current))
                        ? undefined
                        : Number(tableHeight.current) - 1
                }
                onNext={onNext}
            >
                <CustomTable
                    id={TABLE_ID}
                    bordered
                    sticky={{
                        getContainer: () =>
                            document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                    }}
                    columns={columns}
                    dataSource={tableData}
                    rowKey={(record: any) => String(record.map_id)}
                    pagination={false}
                    scroll={{ x: '100%' }}
                    showSorterTooltip={false}
                    style={{
                        opacity: tableDataLoading ? '0' : 1,
                        pointerEvents: tableDataLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: tableDataLoading ? <></> : 'No records found',
                    }}
                />
            </InfiniteScroller>
            <Footer type="default" items={[footerText, '', '', '']} />

            {/* Manage Custom Group modal */}
            <CustomModal
                title="Manage Custom Group"
                open={modal.isModalOpen}
                onCancel={modal.closeModal}
                width={1150}
                destroyOnClose
                cancelButtonProps={{ style: { borderRadius: 6 } }}
                okButtonProps={{
                    disabled:
                        modal.selectedItems.length === 0 ||
                        !modal.selectedGroupValue ||
                        (modal.isCreatingNew && !modal.newGroupName.trim()) ||
                        modal.upsertLoading,
                    style: { borderRadius: 6 },
                    type: 'primary',
                }}
                okText={
                    modal.upsertLoading ? (
                        <Spin size="small" />
                    ) : modal.isCreatingNew ? (
                        'Create Group'
                    ) : modal.editingRecord ? (
                        'Update Group'
                    ) : (
                        'Save'
                    )
                }
                onOk={modal.handleSave}
            >
                <div style={{ display: 'flex', flexDirection: 'column', minHeight: 460 }}>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                            <p className="font-xs mb-1" style={{ color: '#595959' }}>
                                <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>Customer
                            </p>
                            <CustomSelect
                                placeholder="Select Customer"
                                value={modal.selectedDim2Id}
                                disabled={!!modal.editingRecord || dim2OptionsLoading}
                                open={dim2OptionsLoading ? false : undefined}
                                suffixIcon={dim2OptionsLoading ? <Spin size="small" /> : <DownSvg />}
                                options={dim2Options.map((d: any) => ({
                                    label: `${d.customer}`,
                                    value: d.dim2_id,
                                }))}
                                style={{ width: '100%' }}
                                onChange={modal.handleCustomerChange}
                                allowClear
                                showSearch
                                filterOption={(input: string, option: any) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <p className="font-xs mb-1" style={{ color: '#595959' }}>
                                Product Category
                            </p>
                            <CustomSelect
                                placeholder="Select Product Category"
                                value={modal.selectedProductCategory}
                                disabled={
                                    !!modal.editingRecord || !modal.selectedDim2Id || modal.productCategoriesLoading
                                }
                                open={modal.productCategoriesLoading ? false : undefined}
                                suffixIcon={modal.productCategoriesLoading ? <Spin size="small" /> : <DownSvg />}
                                options={modal.productCategories.map((c: string) => ({ label: c, value: c }))}
                                style={{ width: '100%' }}
                                onChange={(val: any) => modal.setSelectedProductCategory(val as string)}
                                allowClear
                                showSearch
                                filterOption={(input: string, option: any) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <p className="font-xs mb-1" style={{ color: '#595959' }}>
                                <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>Group
                            </p>
                            <CustomSelect
                                placeholder="Select Group"
                                value={modal.selectedGroupValue}
                                disabled={
                                    !!modal.editingRecord ||
                                    !modal.selectedDim2Id ||
                                    !modal.selectedProductCategory ||
                                    modal.customerGroupsLoading
                                }
                                open={modal.customerGroupsLoading ? false : undefined}
                                suffixIcon={modal.customerGroupsLoading ? <Spin size="small" /> : <DownSvg />}
                                options={modal.groupSelectOptions}
                                style={{ width: '100%' }}
                                onChange={modal.handleGroupChange}
                                showSearch
                                allowClear
                                filterOption={(input: string, option: any) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <p className="font-xs mb-1" style={{ color: '#595959' }}>
                                <span style={{ color: '#ff4d4f', marginRight: 4 }}>*</span>
                                {modal.isCreatingNew ? 'New Group Name' : 'Group Name'}
                            </p>
                            <Input
                                placeholder="e.g. Group - 1"
                                value={modal.newGroupName}
                                disabled={!modal.selectedGroupValue || modal.customerGroupsLoading}
                                onChange={(e) => modal.setNewGroupName(e.target.value)}
                                style={{ borderRadius: 6 }}
                            />
                        </div>
                    </div>

                    {/* Body */}
                    {modal.editFlowLoading ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 320,
                            }}
                        >
                            <Spin size="small" />
                        </div>
                    ) : !modal.showGroupFields ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#fafafa',
                                borderRadius: 8,
                                border: '1px solid #f0f0f0',
                                color: '#8c8c8c',
                                fontSize: 13,
                                minHeight: 320,
                            }}
                        >
                            Select a customer and group to view material groups
                        </div>
                    ) : modal.dim1Loading ? (
                        <div
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 320,
                            }}
                        >
                            <Spin size="small" />
                        </div>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1 }}>
                                {modalFilterKeys.length > 0 && modal.selectedDim2Id != null && (
                                    <>
                                        <div className="flex justify-end items-center mb-1">
                                            <NewGlobalFilter
                                                availableFilters={modalFilterKeys}
                                                allCheckedAttribute={modal.modalFilter.allCheckedAttribute}
                                                allSelectedValueForAllAttribute={
                                                    modal.modalFilter.allSelectedValueForAllAttribute
                                                }
                                                applyFilterObject={modal.modalFilter.applyFilterObject}
                                                resetFilterObject={modal.modalFilter.reset}
                                                onSubmitHandler={modal.modalFilter.onSave}
                                                url={customGroupsUrls.getDim1AttributeNamesUrl}
                                                selectedFilter={modal.modalFilter.selectedFilter}
                                                setSelectedFilter={modal.modalFilter.setSelectedFilter}
                                                tempApplyFilterObject={modal.modalFilter.tempApplyFilterObject}
                                                setTempApplyFilterObject={modal.modalFilter.setTempApplyFilterObject}
                                                apiVariables={modalAttrNamesApiVars}
                                                filterDimensionInfo={modalFilterDimInfo}
                                                showAttributePanelToLeft
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <FilterRibbon
                                                allCheckedAttribute={modal.modalFilter.allCheckedAttribute}
                                                allSelectedValueForAllAttribute={
                                                    modal.modalFilter.allSelectedValueForAllAttribute
                                                }
                                                applyFilterObject={modal.modalFilter.applyFilterObject}
                                                resetFilterObject={modal.modalFilter.reset}
                                                onSubmitHandler={modal.modalFilter.onSave}
                                                filterKeys={modalFilterKeys}
                                            />
                                        </div>
                                    </>
                                )}

                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0, flex: 1 }}>
                                    <div style={{ flex: '0.6 1 0%', minWidth: 0 }}>
                                        <p
                                            className="font-xs mb-1"
                                            style={{
                                                color: '#595959',
                                                background: '#fafafa',
                                                border: '1px solid #f0f0f0',
                                                borderBottom: 'none',
                                                padding: '6px 12px',
                                                borderRadius: '4px 4px 0 0',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Available ({modal.dim1List.length})
                                        </p>
                                        <ModalTable
                                            rowSelection={{
                                                type: 'checkbox',
                                                selectedRowKeys: modal.availableItemCheckedKeys,
                                                onChange: (keys: React.Key[]) =>
                                                    modal.setAvailableItemCheckedKeys(keys),
                                            }}
                                            columns={availableItemColumns}
                                            dataSource={modal.dim1List}
                                            rowKey="dim1_id"
                                            pagination={false}
                                            showSorterTooltip={false}
                                            scroll={{ y: 300 }}
                                            size="small"
                                            bordered
                                            locale={{ emptyText: 'No items available' }}
                                        />
                                    </div>

                                    {/* Arrow controls */}
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '0 10px',
                                            minHeight: 200,
                                            alignSelf: 'center',
                                        }}
                                    >
                                        <Button
                                            icon={<RightOutlined />}
                                            size="small"
                                            disabled={modal.availableItemCheckedKeys.length === 0}
                                            onClick={modal.moveToSelected}
                                        />
                                        <Button
                                            icon={<LeftOutlined />}
                                            size="small"
                                            disabled={modal.selectedItemCheckedKeys.length === 0}
                                            onClick={modal.moveToAvailable}
                                        />
                                    </div>

                                    <div style={{ flex: '0.4 1 0%', minWidth: 0 }}>
                                        <p
                                            className="font-xs mb-1"
                                            style={{
                                                color: '#595959',
                                                background: '#fafafa',
                                                border: '1px solid #f0f0f0',
                                                borderBottom: 'none',
                                                padding: '6px 12px',
                                                borderRadius: '4px 4px 0 0',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Selected ({modal.selectedItems.length})
                                        </p>
                                        <ModalTable
                                            rowSelection={{
                                                type: 'checkbox',
                                                selectedRowKeys: modal.selectedItemCheckedKeys,
                                                onChange: (keys: React.Key[]) => modal.setSelectedItemCheckedKeys(keys),
                                            }}
                                            columns={selectedItemColumns}
                                            dataSource={modal.selectedItems}
                                            rowKey="dim1_id"
                                            pagination={false}
                                            showSorterTooltip={false}
                                            scroll={{ y: 300 }}
                                            size="small"
                                            bordered
                                            locale={{ emptyText: 'No items selected' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </CustomModal>

            {/* Delete confirmation modal */}
            <ConfirmationModal
                open={!!deleteHandler.recordToDelete}
                onCancel={() => deleteHandler.setRecordToDelete(null)}
                onSubmitClick={deleteHandler.confirmDelete}
                loading={deleteHandler.deleteLoading || deleteHandler.previewLoading}
                title="Are you sure you want to remove this record?"
                subTitle={
                    deleteHandler.previewLoading
                        ? ''
                        : deleteHandler.isLastItem
                        ? `Removing this item "${
                              deleteHandler.recordToDelete?.material_group ?? 'this material group'
                          }" will remove the custom group "${deleteHandler.recordToDelete?.group_name ?? ''}" as well.`
                        : `This will remove "${
                              deleteHandler.recordToDelete?.material_group ?? 'this material group'
                          }" from custom group "${deleteHandler.recordToDelete?.group_name ?? ''}".`
                }
                description={
                    deleteHandler.previewLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                            <Spin size="small" />
                        </div>
                    ) : (
                        ''
                    )
                }
                okText="Delete"
                okButtonProps={{
                    danger: true,
                    type: 'primary',
                    style: { borderRadius: 6 },
                    loading: deleteHandler.deleteLoading,
                    disabled: deleteHandler.previewLoading,
                }}
            />
        </div>
    );
};

export default CustomProductGroupContainer;
